import { useSyncExternalStore } from "react";
import type { Conversation } from "../api/types";

/**
 * Desktop (browser) notifications for incoming calls and texts while the app is open in a background
 * tab or minimized window. The browser permission is per site; on top of it each person can switch
 * notifications off in this browser (stored locally — a per-device convenience, not account state).
 */
const PREF_KEY = "voxora.notifications";
const DISMISS_KEY = "voxora.notifications.prompt-dismissed";

export type NotifyState = "unsupported" | "blocked" | "off" | "on" | "ask";

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function write(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* storage unavailable: preference lasts for this page only */ }
}

export const notificationsSupported = () => typeof window !== "undefined" && "Notification" in window && window.isSecureContext;

export function notifyState(): NotifyState {
  if (!notificationsSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission === "default") return "ask";
  return read(PREF_KEY) === "off" ? "off" : "on";
}

/** Must be called from a click: browsers only show the permission prompt for a user gesture. */
export async function enableNotifications(): Promise<NotifyState> {
  if (!notificationsSupported()) return "unsupported";
  if (Notification.permission === "default") await Notification.requestPermission();
  write(PREF_KEY, "on");
  emit();
  return notifyState();
}

export function disableNotifications() {
  write(PREF_KEY, "off");
  emit();
}

export const promptDismissed = () => read(DISMISS_KEY) === "1";
export function dismissPrompt() {
  write(DISMISS_KEY, "1");
  emit();
}

export function useNotifyState() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      // Permission can change in browser settings; re-check when the user returns to the tab.
      const onFocus = () => cb();
      window.addEventListener("focus", onFocus);
      return () => { listeners.delete(cb); window.removeEventListener("focus", onFocus); };
    },
    () => `${notifyState()}|${promptDismissed() ? 1 : 0}`,
    () => "unsupported|0",
  );
}

/** True when the person isn't looking at the app (other tab, other window, or minimized). */
export const appInBackground = () => document.visibilityState === "hidden" || !document.hasFocus();

export function showNotification(title: string, opts: { body: string; tag: string; sticky?: boolean; onClick?: () => void }): Notification | null {
  if (notifyState() !== "on") return null;
  try {
    const n = new Notification(title, { body: opts.body, tag: opts.tag, icon: "/brand/atlantech/app-icon-192.png", requireInteraction: opts.sticky ?? false });
    n.onclick = () => { window.focus(); opts.onClick?.(); n.close(); };
    return n;
  } catch {
    return null; // e.g. Android Chrome only allows notifications from a service worker
  }
}

/**
 * Conversations with a new inbound text since the last poll that are yours to answer (assigned to you,
 * or unassigned in the shared inbox). The first poll (`seen` null) only records a baseline.
 */
export function newInboundTexts(seen: Map<string, string> | null, items: Conversation[], myMembershipId: string | null) {
  const next = new Map(seen ?? []);
  const fresh: Conversation[] = [];
  for (const c of items) {
    const before = next.get(c.id);
    next.set(c.id, c.lastMessageAt);
    if (seen === null || (before !== undefined && before >= c.lastMessageAt)) continue;
    if (c.lastDirection !== "INBOUND" || c.unread === 0) continue;
    if (c.assignedTo && c.assignedTo.membershipId !== myMembershipId) continue;
    fresh.push(c);
  }
  return { fresh, seen: next };
}
